/**
 * Internal-linking clusters for marketing/SEO pages. Rendered by
 * <RelatedLinks> (injected once in MarketingShell) so every landing page
 * and blog post links to its topical siblings — passing link equity around
 * the cluster and helping Google understand the topic groups.
 *
 * A page's "related" links = the other pages in the first cluster that
 * contains its path. Paths are locale-less (no /zh prefix); RelatedLinks
 * adds the prefix per current locale. Add a page to a cluster + give it a
 * LABEL and it auto-appears in its siblings' Related sections.
 */
import { type MarketingLocale, LOCALE_PREFIX } from '../i18n/marketingLocales';

interface Cluster {
  heading: { en: string; zh: string };
  paths: string[];
}

const LABELS: Record<string, { en: string; zh: string }> = {
  '/decision-log': { en: 'Decision Log', zh: '决策日志' },
  '/decision-log-template': { en: 'Decision Log Template', zh: '决策日志模板' },
  '/architecture-decision-record': { en: 'Architecture Decision Records', zh: '架构决策记录 (ADR)' },
  '/ai-prd': { en: 'AI PRD Tool', zh: 'AI PRD 工具' },
  '/comparisons/chatprd': { en: 'Draftlize vs ChatPRD', zh: 'Draftlize vs ChatPRD 对比' },
  '/product-requirements-document': { en: 'Product Requirements Document', zh: '产品需求文档 (PRD)' },
  '/prd-template': { en: 'PRD Template', zh: 'PRD 模板' },
  '/product-brief-template': { en: 'Product Brief Template', zh: '产品简报模板' },
  '/product-spec-template': { en: 'Product Spec Template', zh: '产品规格模板' },
  '/spec-driven-development': { en: 'Spec-Driven Development', zh: 'Spec 驱动开发' },
  '/living-documentation': { en: 'Living Documentation', zh: '活文档' },
  '/prfaq': { en: 'PR/FAQ Template', zh: 'PR/FAQ 模板' },
  '/competitive-analysis-template': { en: 'Competitive Analysis Template', zh: '竞品分析模板' },
  '/go-to-market-strategy-template': { en: 'Go-to-Market Template', zh: 'GTM 策略模板' },
  '/product-launch-checklist': { en: 'Product Launch Checklist', zh: '产品发布清单' },
  '/bug-report-template': { en: 'Bug Report Template', zh: 'Bug 报告模板' },
  '/technical-design-document': { en: 'Technical Design Doc Template', zh: '技术设计文档模板' },
  '/release-plan-template': { en: 'Release Plan Template', zh: '发布计划模板' },
  '/feature-request-template': { en: 'Feature Request Template', zh: '功能请求模板' },
  '/product-roadmap-example': { en: 'Product Roadmap Examples', zh: '产品路线图示例' },
  '/what-is-a-product-roadmap': { en: 'What Is a Product Roadmap?', zh: '产品路线图是什么' },
  '/content-roadmap': { en: 'What Is a Content Roadmap?', zh: '内容路线图是什么' },
  '/product-strategy': { en: 'What Is Product Strategy?', zh: '产品策略是什么' },
  '/product-discovery': { en: 'Product Discovery', zh: '产品发现' },
  '/what-is-product-management': { en: 'What Is Product Management?', zh: '产品管理是什么' },
  '/prioritization-frameworks': { en: 'Prioritization Frameworks', zh: '优先级框架' },
  '/best-knowledge-base-software': { en: 'Best Knowledge Base Software', zh: '知识库软件清单' },
  '/release-notes': { en: 'Release Notes + Template', zh: '发布说明与模板' },
  '/user-story-template': { en: 'User Story Template', zh: '用户故事模板' },
  '/acceptance-criteria': { en: 'Acceptance Criteria', zh: '验收标准' },
  '/swot-analysis-template': { en: 'SWOT Analysis Template', zh: 'SWOT 分析模板' },
  '/creative-brief-template': { en: 'Creative Brief Template', zh: '创意简报模板' },
  '/project-proposal-template': { en: 'Project Proposal Template', zh: '项目提案模板' },
  '/business-roadmap': { en: 'What Is a Business Roadmap?', zh: '业务路线图是什么' },
  '/technical-documentation-template': { en: 'Technical Documentation Template', zh: '技术文档模板' },
  '/from-meeting-notes-to-decisions': { en: 'Meeting Notes → Decisions', zh: '会议记录→决策' },
  '/product-management-metrics': { en: 'Product Management Metrics', zh: '产品管理指标' },
  '/best-ai-tools-for-product-managers': { en: 'Best AI Tools for PMs', zh: 'PM 的 AI 工具清单' },
  '/best-product-management-software': { en: 'Best PM Software', zh: '产品管理软件清单' },
  '/best-ai-note-taking-app': { en: 'Best AI Note-Taking Apps', zh: 'AI 笔记 App 清单' },
  '/comparisons/chatgpt': { en: 'Draftlize vs ChatGPT', zh: 'Draftlize vs ChatGPT' },
  '/comparisons/notion-ai': { en: 'Notion Alternative', zh: 'Notion 替代方案' },
  '/comparisons/coda': { en: 'Coda Alternative', zh: 'Coda 替代方案' },
  '/comparisons/linear-docs': { en: 'Linear Alternative', zh: 'Linear 替代方案' },
  '/comparisons/obsidian': { en: 'Obsidian Alternative', zh: 'Obsidian 替代方案' },
  '/blog/what-is-a-prd': { en: 'What is a PRD?', zh: '什么是 PRD' },
  '/blog/how-to-write-a-prd': { en: 'How to Write a PRD', zh: '怎么写 PRD' },
  '/blog/decision-logs-in-the-ai-era': { en: 'Decision Logs in the AI Era', zh: 'AI 时代的决策日志' },
  '/blog/obsidian-vs-notion': { en: 'Obsidian vs Notion', zh: 'Obsidian vs Notion' },
  '/blog/what-do-product-managers-do': { en: 'What Do PMs Do?', zh: '产品经理做什么' },
  '/blog/customer-feedback-analysis': { en: 'Customer Feedback Analysis', zh: '客户反馈分析' },
  '/blog/competitor-analysis-framework': { en: 'Competitor Analysis Frameworks', zh: '竞品分析框架' },
  '/blog/context-engineering': { en: 'Context Engineering', zh: '上下文工程' },
  '/blog/llm-context-window': { en: 'LLM Context Window', zh: 'LLM 上下文窗口' },
};

// Clusters are kept topically tight (≤~9 paths). RelatedLinks renders up to 8
// siblings (see getRelated), so a tight cluster fully interlinks AND sends a
// stronger topical-relevance signal than a 16-path grab-bag. Order matters —
// only the first 8 siblings render, so each cluster is ordered hub-first /
// highest-value-first (e.g. the roadmap hub reaches /prioritization-frameworks).
const CLUSTERS: Cluster[] = [
  {
    heading: { en: 'Roadmap & strategy', zh: '路线图与策略' },
    paths: ['/what-is-a-product-roadmap', '/product-roadmap-example', '/product-strategy', '/product-discovery', '/prioritization-frameworks', '/business-roadmap', '/what-is-product-management', '/product-management-metrics', '/content-roadmap'],
  },
  {
    heading: { en: 'PRDs & specs', zh: 'PRD 与规格' },
    paths: ['/product-requirements-document', '/prd-template', '/product-spec-template', '/product-brief-template', '/ai-prd', '/comparisons/chatprd', '/prfaq', '/blog/what-is-a-prd', '/blog/how-to-write-a-prd'],
  },
  {
    heading: { en: 'Decision tracking', zh: '决策追踪' },
    paths: ['/decision-log', '/decision-log-template', '/architecture-decision-record', '/blog/decision-logs-in-the-ai-era'],
  },
  {
    heading: { en: 'Spec & delivery templates', zh: '规格与交付模板' },
    paths: ['/user-story-template', '/acceptance-criteria', '/bug-report-template', '/feature-request-template', '/release-notes', '/release-plan-template', '/technical-design-document', '/technical-documentation-template'],
  },
  {
    heading: { en: 'Strategy & launch templates', zh: '策略与发布模板' },
    paths: ['/competitive-analysis-template', '/go-to-market-strategy-template', '/product-launch-checklist', '/swot-analysis-template', '/creative-brief-template', '/project-proposal-template', '/blog/competitor-analysis-framework'],
  },
  {
    heading: { en: 'Compare & choose tools', zh: '工具对比与选择' },
    paths: ['/best-ai-tools-for-product-managers', '/comparisons/chatprd', '/best-product-management-software', '/best-knowledge-base-software', '/comparisons/notion-ai', '/comparisons/linear-docs', '/comparisons/chatgpt', '/comparisons/obsidian', '/comparisons/coda', '/best-ai-note-taking-app', '/from-meeting-notes-to-decisions', '/blog/obsidian-vs-notion'],
  },
  {
    heading: { en: 'More PM guides', zh: '更多 PM 指南' },
    paths: ['/blog/what-do-product-managers-do', '/blog/customer-feedback-analysis', '/blog/context-engineering', '/blog/llm-context-window'],
  },
  {
    heading: { en: 'Spec-driven development', zh: 'Spec 驱动开发' },
    paths: ['/spec-driven-development', '/living-documentation', '/product-spec-template', '/technical-design-document', '/technical-documentation-template', '/architecture-decision-record', '/decision-log'],
  },
];

export interface RelatedResult {
  heading: string;
  links: Array<{ href: string; label: string }>;
}

/** Cluster paths that actually exist under a non-EN/ZH locale prefix. EN and
 *  ZH have complete coverage (every cluster path exists in both); the other
 *  locales only have a handful of pages, so a sibling link may point at a
 *  page that was never built. Consulted by getRelated: siblings in this set
 *  keep the locale prefix, everything else falls back to the EN URL —
 *  a cross-language link beats an internal 404. */
const LOCALE_PATHS: Partial<Record<MarketingLocale, ReadonlySet<string>>> = {
  zhtw: new Set([
    '/decision-log',
    '/decision-log-template',
    '/product-brief-template',
    '/product-discovery',
    '/comparisons/notion-ai',
    '/comparisons/coda',
  ]),
  ja: new Set(['/decision-log', '/product-discovery', '/comparisons/notion-ai', '/comparisons/obsidian']),
  ko: new Set(['/decision-log', '/product-discovery', '/comparisons/notion-ai', '/comparisons/obsidian']),
};

/** Related links for a page = other pages in its first matching cluster.
 *  Clusters are EN/ZH only today, so label/heading text falls back to those
 *  two; ja/de/… pages (when added to a cluster) take EN text but the correct
 *  locale URL prefix. Single-locale pages aren't in any cluster → null. */
export function getRelated(
  path: string | undefined,
  locale: MarketingLocale,
): RelatedResult | null {
  if (!path) return null;
  const cluster = CLUSTERS.find((c) => c.paths.includes(path));
  if (!cluster) return null;
  const others = cluster.paths.filter((p) => p !== path).slice(0, 8);
  if (others.length === 0) return null;
  const textLoc: 'en' | 'zh' = locale === 'zh' || locale === 'zhtw' ? 'zh' : 'en';
  const complete = locale === 'en' || locale === 'zh';
  const localePaths = LOCALE_PATHS[locale];
  return {
    heading: cluster.heading[textLoc],
    links: others.map((p) => {
      const prefixed = complete || localePaths?.has(p);
      return {
        href: (prefixed ? LOCALE_PREFIX[locale] : '') + p,
        label: (LABELS[p] ?? { en: p, zh: p })[textLoc],
      };
    }),
  };
}
